/**
 * cardPatterns.js
 * Seamless image tile pattern library and utilities for TrophyCoin Card Template Designer.
 * Provides ~20 built-in crisp SVG patterns and persistence helpers for user-uploaded custom tiles.
 */

// Helper to produce a clean URL-safe SVG data URI (percent-encoding parens and single quotes for CSS url() safety)
function svgToDataUri(svgString) {
  return `data:image/svg+xml;utf8,${encodeURIComponent(svgString.trim())
    .replace(/\(/g, '%28')
    .replace(/\)/g, '%29')
    .replace(/'/g, '%27')}`;
}

export const DEFAULT_CARD_PATTERNS = [
  {
    id: 'carbon_fiber',
    name: 'Carbon Fiber',
    category: 'Carbon & Mesh',
    tileWidth: 16,
    tileHeight: 16,
    defaultScale: 100,
    defaultTintColor: '#18181c',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
        <rect width="16" height="16" fill="#18181c"/>
        <rect x="0" y="0" width="8" height="8" fill="#26262e"/>
        <rect x="8" y="8" width="8" height="8" fill="#26262e"/>
        <path d="M0,0 L8,8 M8,0 L16,8 M0,8 L8,16 M8,8 L16,16" stroke="#363642" stroke-width="1.2"/>
        <path d="M4,0 L8,4 M0,4 L4,8 M12,0 L16,4 M8,4 L12,8 M4,8 L8,12 M0,12 L4,16 M12,8 L16,12 M8,12 L12,16" stroke="#0e0e12" stroke-width="0.8"/>
      </svg>
    `)
  },
  {
    id: 'honeycomb',
    name: 'Honeycomb Hex',
    category: 'Carbon & Mesh',
    tileWidth: 28,
    tileHeight: 48,
    defaultScale: 100,
    defaultTintColor: '#131317',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="48" viewBox="0 0 28 48">
        <path d="M 0,12 L 14,4 L 28,12 L 28,28 L 14,36 L 0,28 Z M 0,36 L 14,44 L 28,36 M 14,44 L 14,48 M 14,0 L 14,4" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="1.5" stroke-linejoin="round"/>
      </svg>
    `)
  },
  {
    id: 'fine_dots',
    name: 'Subtle Dot Grid',
    category: 'Geometric',
    tileWidth: 16,
    tileHeight: 16,
    defaultScale: 100,
    defaultTintColor: '#18181b',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
        <circle cx="8" cy="8" r="1.8" fill="rgba(255,255,255,0.35)"/>
        <circle cx="0" cy="0" r="1.2" fill="rgba(255,255,255,0.2)"/>
        <circle cx="16" cy="0" r="1.2" fill="rgba(255,255,255,0.2)"/>
        <circle cx="0" cy="16" r="1.2" fill="rgba(255,255,255,0.2)"/>
        <circle cx="16" cy="16" r="1.2" fill="rgba(255,255,255,0.2)"/>
      </svg>
    `)
  },
  {
    id: 'graph_paper',
    name: 'Graph Paper',
    category: 'Geometric',
    tileWidth: 20,
    tileHeight: 20,
    defaultScale: 100,
    defaultTintColor: '#111827',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
        <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="1"/>
      </svg>
    `)
  },
  {
    id: 'blueprint_grid',
    name: 'Blueprint Grid',
    category: 'Geometric',
    tileWidth: 40,
    tileHeight: 40,
    defaultScale: 100,
    defaultTintColor: '#0f172a',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
        <path d="M 10 0 L 10 40 M 20 0 L 20 40 M 30 0 L 30 40 M 0 10 L 40 10 M 0 20 L 40 20 M 0 30 L 40 30" fill="none" stroke="rgba(255,255,255,0.08)" stroke-width="1"/>
        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.28)" stroke-width="1.5"/>
      </svg>
    `)
  },
  {
    id: 'diagonal_stripes_right',
    name: 'Diagonal Lines 45°',
    category: 'Geometric',
    tileWidth: 16,
    tileHeight: 16,
    defaultScale: 100,
    defaultTintColor: '#1e293b',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
        <path d="M -4,4 L 4,-4 M 0,16 L 16,0 M 12,20 L 20,12" fill="none" stroke="rgba(255,255,255,0.28)" stroke-width="2"/>
      </svg>
    `)
  },
  {
    id: 'diagonal_stripes_left',
    name: 'Diagonal Lines -45°',
    category: 'Geometric',
    tileWidth: 16,
    tileHeight: 16,
    defaultScale: 100,
    defaultTintColor: '#1e293b',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
        <path d="M -4,12 L 4,20 M 0,0 L 16,16 M 12,-4 L 20,4" fill="none" stroke="rgba(255,255,255,0.28)" stroke-width="2"/>
      </svg>
    `)
  },
  {
    id: 'crosshatch',
    name: 'Woven Crosshatch',
    category: 'Carbon & Mesh',
    tileWidth: 16,
    tileHeight: 16,
    defaultScale: 100,
    defaultTintColor: '#18181b',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
        <path d="M -4,4 L 4,-4 M 0,16 L 16,0 M 12,20 L 20,12" fill="none" stroke="rgba(255,255,255,0.22)" stroke-width="1.5"/>
        <path d="M -4,12 L 4,20 M 0,0 L 16,16 M 12,-4 L 20,4" fill="none" stroke="rgba(255,255,255,0.22)" stroke-width="1.5"/>
      </svg>
    `)
  },
  {
    id: 'fish_scales',
    name: 'Fish Scales (Marine)',
    category: 'Organic & Surface',
    tileWidth: 32,
    tileHeight: 16,
    defaultScale: 100,
    defaultTintColor: '#064e3b',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="16" viewBox="0 0 32 16">
        <path d="M 0,0 A 16,16 0 0,0 32,0 M -16,16 A 16,16 0 0,0 16,16 M 16,16 A 16,16 0 0,0 48,16" fill="none" stroke="rgba(255,255,255,0.32)" stroke-width="1.5"/>
      </svg>
    `)
  },
  {
    id: 'water_waves',
    name: 'Water Waves',
    category: 'Organic & Surface',
    tileWidth: 40,
    tileHeight: 20,
    defaultScale: 100,
    defaultTintColor: '#075985',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="20" viewBox="0 0 40 20">
        <path d="M 0,6 Q 10,0 20,6 T 40,6 M 0,16 Q 10,10 20,16 T 40,16" fill="none" stroke="rgba(255,255,255,0.28)" stroke-width="1.5"/>
      </svg>
    `)
  },
  {
    id: 'diamond_plate',
    name: 'Steel Diamond Plate',
    category: 'Tech & Metal',
    tileWidth: 32,
    tileHeight: 32,
    defaultScale: 100,
    defaultTintColor: '#27272a',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
        <path d="M 12,16 Q 14,12 16,12 Q 18,12 20,16 Q 18,20 16,20 Q 14,20 12,16 Z" fill="rgba(255,255,255,0.4)" stroke="rgba(0,0,0,0.6)" stroke-width="1"/>
        <path d="M -4,0 Q -2,-4 0,-4 Q 2,-4 4,0 Q 2,4 0,4 Q -2,4 -4,0 Z" fill="rgba(255,255,255,0.4)" stroke="rgba(0,0,0,0.6)" stroke-width="1"/>
        <path d="M 28,0 Q 30,-4 32,-4 Q 34,-4 36,0 Q 34,4 32,4 Q 30,4 28,0 Z" fill="rgba(255,255,255,0.4)" stroke="rgba(0,0,0,0.6)" stroke-width="1"/>
        <path d="M -4,32 Q -2,28 0,28 Q 2,28 4,32 Q 2,36 0,36 Q -2,36 -4,32 Z" fill="rgba(255,255,255,0.4)" stroke="rgba(0,0,0,0.6)" stroke-width="1"/>
        <path d="M 28,32 Q 30,28 32,28 Q 34,28 36,32 Q 34,36 32,36 Q 30,36 28,32 Z" fill="rgba(255,255,255,0.4)" stroke="rgba(0,0,0,0.6)" stroke-width="1"/>
      </svg>
    `)
  },
  {
    id: 'brushed_metal',
    name: 'Brushed Metal',
    category: 'Tech & Metal',
    tileWidth: 40,
    tileHeight: 20,
    defaultScale: 100,
    defaultTintColor: '#3f3f46',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="20" viewBox="0 0 40 20">
        <line x1="0" y1="2" x2="40" y2="2" stroke="rgba(255,255,255,0.14)" stroke-width="1"/>
        <line x1="0" y1="5" x2="40" y2="5" stroke="rgba(0,0,0,0.2)" stroke-width="1"/>
        <line x1="0" y1="8" x2="40" y2="8" stroke="rgba(255,255,255,0.22)" stroke-width="1.2"/>
        <line x1="0" y1="11" x2="40" y2="11" stroke="rgba(0,0,0,0.18)" stroke-width="1"/>
        <line x1="0" y1="14" x2="40" y2="14" stroke="rgba(255,255,255,0.18)" stroke-width="1"/>
        <line x1="0" y1="17" x2="40" y2="17" stroke="rgba(0,0,0,0.22)" stroke-width="1.2"/>
        <line x1="0" y1="20" x2="40" y2="20" stroke="rgba(255,255,255,0.14)" stroke-width="1"/>
      </svg>
    `)
  },
  {
    id: 'wood_grain',
    name: 'Wood Grain',
    category: 'Organic & Surface',
    tileWidth: 30,
    tileHeight: 60,
    defaultScale: 100,
    defaultTintColor: '#451a03',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="30" height="60" viewBox="0 0 30 60">
        <path d="M 6,0 Q 10,30 6,60 M 16,0 Q 11,20 19,40 Q 23,50 16,60 M 26,0 Q 21,30 26,60" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="1.5"/>
      </svg>
    `)
  },
  {
    id: 'leather_grain',
    name: 'Pebble Leather Grain',
    category: 'Organic & Surface',
    tileWidth: 24,
    tileHeight: 24,
    defaultScale: 100,
    defaultTintColor: '#1c1917',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <circle cx="4" cy="4" r="1.5" fill="rgba(255,255,255,0.25)"/>
        <circle cx="16" cy="6" r="2.2" fill="rgba(255,255,255,0.2)"/>
        <circle cx="8" cy="14" r="1.8" fill="rgba(255,255,255,0.22)"/>
        <circle cx="20" cy="16" r="1.5" fill="rgba(255,255,255,0.28)"/>
        <circle cx="12" cy="22" r="2" fill="rgba(255,255,255,0.18)"/>
        <circle cx="2" cy="20" r="1.2" fill="rgba(255,255,255,0.25)"/>
      </svg>
    `)
  },
  {
    id: 'camo_woodland',
    name: 'Tactical Camouflage',
    category: 'Organic & Surface',
    tileWidth: 48,
    tileHeight: 48,
    defaultScale: 100,
    defaultTintColor: '#14532d',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <rect width="48" height="48" fill="#14532d"/>
        <path d="M 0,0 Q 8,14 16,8 Q 24,2 28,16 Q 18,24 10,20 Q 2,24 0,14 Z" fill="#166534"/>
        <path d="M 32,24 Q 44,20 48,32 Q 40,44 28,40 Q 20,44 26,30 Z" fill="#3f2e16"/>
        <path d="M 28,0 Q 36,4 44,0 Q 48,10 40,16 Q 30,12 28,0 Z" fill="#052e16"/>
        <path d="M 0,36 Q 10,32 14,42 Q 6,48 0,46 Z" fill="#365314"/>
      </svg>
    `)
  },
  {
    id: 'houndstooth',
    name: 'Classic Houndstooth',
    category: 'Geometric',
    tileWidth: 32,
    tileHeight: 32,
    defaultScale: 100,
    defaultTintColor: '#18181b',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
        <polygon points="0,0 16,0 16,16 0,16" fill="rgba(255,255,255,0.28)"/>
        <polygon points="16,16 32,16 32,32 16,32" fill="rgba(255,255,255,0.28)"/>
        <polygon points="16,0 24,0 8,16 0,16" fill="rgba(255,255,255,0.28)"/>
        <polygon points="32,16 32,24 16,8 16,0" fill="rgba(255,255,255,0.28)"/>
      </svg>
    `)
  },
  {
    id: 'circuit_board',
    name: 'Circuit Board PCB',
    category: 'Tech & Metal',
    tileWidth: 40,
    tileHeight: 40,
    defaultScale: 100,
    defaultTintColor: '#022c22',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
        <path d="M 0,20 L 14,20 L 20,26 L 40,26 M 20,0 L 20,10 L 26,16 L 26,40 M 8,0 L 8,12 L 0,20" fill="none" stroke="rgba(52,211,153,0.5)" stroke-width="1.5"/>
        <circle cx="14" cy="20" r="2.5" fill="rgba(52,211,153,0.8)"/>
        <circle cx="26" cy="16" r="2.5" fill="rgba(52,211,153,0.8)"/>
        <circle cx="34" cy="26" r="2" fill="rgba(52,211,153,0.8)"/>
      </svg>
    `)
  },
  {
    id: 'chevron',
    name: 'Chevron Zigzag',
    category: 'Geometric',
    tileWidth: 24,
    tileHeight: 16,
    defaultScale: 100,
    defaultTintColor: '#1e1b4b',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="16" viewBox="0 0 24 16">
        <path d="M 0,8 L 12,0 L 24,8 L 12,16 Z" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="1.5"/>
      </svg>
    `)
  },
  {
    id: 'noise_sand',
    name: 'Subtle Granite Noise',
    category: 'Organic & Surface',
    tileWidth: 32,
    tileHeight: 32,
    defaultScale: 100,
    defaultTintColor: '#18181b',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
        <rect x="2" y="3" width="1" height="1" fill="rgba(255,255,255,0.3)"/>
        <rect x="9" y="8" width="1.5" height="1.5" fill="rgba(255,255,255,0.2)"/>
        <rect x="18" y="4" width="1" height="1" fill="rgba(255,255,255,0.35)"/>
        <rect x="27" y="10" width="1" height="1" fill="rgba(255,255,255,0.25)"/>
        <rect x="5" y="19" width="1.2" height="1.2" fill="rgba(255,255,255,0.25)"/>
        <rect x="14" y="24" width="1" height="1" fill="rgba(255,255,255,0.3)"/>
        <rect x="22" y="18" width="1.5" height="1.5" fill="rgba(255,255,255,0.2)"/>
        <rect x="30" y="28" width="1" height="1" fill="rgba(255,255,255,0.35)"/>
      </svg>
    `)
  },
  {
    id: 'starfield',
    name: 'Starfield & Starlight',
    category: 'Organic & Surface',
    tileWidth: 48,
    tileHeight: 48,
    defaultScale: 100,
    defaultTintColor: '#09090b',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <!-- 4-point star -->
        <path d="M 12,10 Q 12,14 16,14 Q 12,14 12,18 Q 12,14 8,14 Q 12,14 12,10 Z" fill="rgba(255,255,255,0.7)"/>
        <path d="M 36,32 Q 36,35 39,35 Q 36,35 36,38 Q 36,35 33,35 Q 36,35 36,32 Z" fill="rgba(255,255,255,0.6)"/>
        <circle cx="38" cy="8" r="1" fill="rgba(255,255,255,0.5)"/>
        <circle cx="6" cy="36" r="0.8" fill="rgba(255,255,255,0.4)"/>
        <circle cx="26" cy="42" r="1.2" fill="rgba(255,255,255,0.5)"/>
      </svg>
    `)
  },
  {
    id: 'brick_masonry',
    name: 'Subway Brick Masonry',
    category: 'Geometric',
    tileWidth: 32,
    tileHeight: 16,
    defaultScale: 100,
    defaultTintColor: '#262626',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="16" viewBox="0 0 32 16">
        <path d="M 0,0 L 32,0 M 0,8 L 32,8 M 0,16 L 32,16 M 0,0 L 0,8 M 16,8 L 16,16 M 32,0 L 32,8" fill="none" stroke="rgba(255,255,255,0.22)" stroke-width="1"/>
      </svg>
    `)
  },
  {
    id: 'isometric_cubes',
    name: 'Isometric 3D Cubes',
    category: 'Geometric',
    tileWidth: 28,
    tileHeight: 48,
    defaultScale: 100,
    defaultTintColor: '#18181b',
    image: svgToDataUri(`
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="48" viewBox="0 0 28 48">
        <polygon points="14,4 28,12 14,20 0,12" fill="rgba(255,255,255,0.35)"/>
        <polygon points="0,12 14,20 14,36 0,28" fill="rgba(255,255,255,0.15)"/>
        <polygon points="14,20 28,12 28,28 14,36" fill="rgba(0,0,0,0.3)"/>
        <polygon points="14,44 28,36 28,48 14,48" fill="rgba(255,255,255,0.15)"/>
        <polygon points="0,36 14,44 14,48 0,48" fill="rgba(0,0,0,0.3)"/>
      </svg>
    `)
  }
];

const CUSTOM_PATTERNS_STORAGE_KEY = 'trophycoin_custom_patterns';

/**
 * Loads custom patterns saved in localStorage.
 */
export function getStoredCustomPatterns() {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(CUSTOM_PATTERNS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.error('Failed to load custom patterns from localStorage:', err);
    return [];
  }
}

/**
 * Saves a new custom pattern to localStorage.
 */
export function saveCustomPattern(pattern) {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    const existing = getStoredCustomPatterns();
    const updated = [pattern, ...existing.filter(p => p.id !== pattern.id)];
    window.localStorage.setItem(CUSTOM_PATTERNS_STORAGE_KEY, JSON.stringify(updated));
    return true;
  } catch (err) {
    console.error('Failed to save custom pattern to localStorage:', err);
    return false;
  }
}

/**
 * Deletes a custom pattern from localStorage by ID.
 */
export function deleteCustomPattern(patternId) {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    const existing = getStoredCustomPatterns();
    const filtered = existing.filter(p => p.id !== patternId);
    window.localStorage.setItem(CUSTOM_PATTERNS_STORAGE_KEY, JSON.stringify(filtered));
    return true;
  } catch (err) {
    console.error('Failed to delete custom pattern from localStorage:', err);
    return false;
  }
}

/**
 * Calculates CSS background styles for a pattern config.
 */
export function getPatternBackgroundStyle(pattern) {
  if (!pattern || !pattern.image) return {};
  const scale = (pattern.scale !== undefined ? pattern.scale : 100) / 100;
  const baseW = pattern.tileWidth || 32;
  const baseH = pattern.tileHeight || 32;
  const scaledW = Math.max(2, Math.round(baseW * scale));
  const scaledH = Math.max(2, Math.round(baseH * scale));
  const repeat = pattern.repeat || 'repeat';
  const tint = pattern.tintColor || 'transparent';

  const opacity = pattern.opacity !== undefined ? (pattern.opacity > 1 ? pattern.opacity / 100 : pattern.opacity) : undefined;

  return {
    backgroundColor: tint !== 'transparent' ? tint : undefined,
    backgroundImage: `url("${pattern.image}")`,
    backgroundRepeat: repeat,
    backgroundSize: `${scaledW}px ${scaledH}px`,
    backgroundPosition: '0 0',
    ...(opacity !== undefined ? { opacity } : {}),
    ...(pattern.blendMode ? { mixBlendMode: pattern.blendMode } : {})
  };
}

/**
 * Generates a CSS shorthand background string from pattern config.
 */
export function generatePatternString(pattern) {
  if (!pattern || !pattern.image) return 'transparent';
  const scale = (pattern.scale !== undefined ? pattern.scale : 100) / 100;
  const baseW = pattern.tileWidth || 32;
  const baseH = pattern.tileHeight || 32;
  const scaledW = Math.max(2, Math.round(baseW * scale));
  const scaledH = Math.max(2, Math.round(baseH * scale));
  const repeat = pattern.repeat || 'repeat';
  const tint = pattern.tintColor || 'transparent';
  
  return `${tint} url("${pattern.image}") ${repeat} 0 0 / ${scaledW}px ${scaledH}px`;
}
